/**
 * SharedMovementReportPage — public, no-auth view served at /m/:token.
 *
 * Reuses the same section components from MovementReportPage. The backend
 * endpoint `/api/movement-report/shared/{token}` redacts user_id / scan_id /
 * original_report_id so this is safe to expose. Token expires in 30 days.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download } from "lucide-react";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";
import {
  HeadlineCard, ZoneMovementTable, InterventionSummary, NarrativeSection,
  downloadMovementReportPdf,
} from "./MovementReportPage";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedMovementReportPage() {
  usePageTitle("Capability Movement Report");
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/movement-report/shared/${token}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="font-dm p-12 text-center text-navy/70">Loading shared report…</div>;
  if (error) return (
    <div className="bg-cream min-h-screen flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-10 max-w-md text-center shadow-sm border border-navy/10">
        <LogoDark width={140} height={40} />
        <h2 className="font-playfair text-navy text-xl font-bold mt-5 mb-2">Link unavailable</h2>
        <p className="font-dm text-navy/60 text-sm mb-5" data-testid="shared-error">{error}</p>
        <Link to="/" className="inline-block bg-navy text-gold px-5 py-2.5 rounded-lg font-dm font-semibold">Back to RoleIQ</Link>
      </div>
    </div>
  );
  if (!report) return null;

  return (
    <div className="bg-cream min-h-screen pb-16" data-testid="shared-movement-report">
      {/* Brand-only header — no auth-linked navigation */}
      <div className="bg-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><LogoDark width={140} height={40} /></Link>
          <span className="font-dm text-gold/80 text-xs uppercase tracking-wider hidden sm:inline">Shared Movement Report</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <HeadlineCard headline={report.headline || {}} />
        <ZoneMovementTable movements={report.zone_movements || []} />
        <InterventionSummary summary={report.intervention_summary || {}} />
        <NarrativeSection title="Movement Narrative" body={report.ai_narrative} testid="ai-narrative" />
        <NarrativeSection title="Business Translation" body={report.business_translation} testid="business-translation" />

        <div className="bg-white border border-navy/10 rounded-2xl p-6 text-center" data-testid="shared-footer">
          <p className="font-dm text-navy/60 text-xs uppercase tracking-wide mb-2">Powered by</p>
          <LogoDark width={120} height={34} />
          <p className="font-dm text-navy/50 text-xs mt-3">
            Shared {new Date(report.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            {" · "} read-only board view
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            <button onClick={() => downloadMovementReportPdf(report)} data-testid="shared-pdf-btn"
              className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 rounded-lg font-dm font-semibold hover:bg-navy/90">
              <Download size={16} /> Download PDF
            </button>
            <Link to="/" className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-lg font-dm font-semibold hover:bg-gold/90">
              Discover RoleIQ →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
